import { validarDeducoes, validarTributosExportacaoEConsumo } from 'utils/validarDeducoes';
import { EMetodoReceitaBrutaCalculada } from '../../../../../../../../../../../enums/EMetodoBaseCalculo';
import { IFormReceitaBrutaCalculada } from '../../../../../../../../../../../interfaces/IReceitaBrutaCalculada';
import { removeFormatCurrency } from '../../../../../../../../../../../utils/formatCurrency';
import { formatFloatToUS } from '../../../../../../../../../../../utils/formatFloat';

const NUMERO_MINIMO = 0.01;
const NUMERO_MAXIMO = 1e11;

const validarCampoTexto = (value?: string): boolean => {
  return value == undefined || value === '';
};

const validarCampoNumero = (value?: string) => {
  if (value) {
    const floatValue = parseFloat(formatFloatToUS(value));

    return floatValue < NUMERO_MINIMO || floatValue >= NUMERO_MAXIMO;
  } else {
    return true;
  }
};

const validarCampoFinanceiro = (value?: string) => {
  return value == undefined || removeFormatCurrency(value) < NUMERO_MINIMO;
};

const validarCodigoNCMNovaEspecie = (value?: string) => {
  return value == undefined || value?.length !== 8;
};

const retornarCamposObrigatorios = (formValues: IFormReceitaBrutaCalculada) => {
  type ValidacaoCampo = {
    field: string;
    label: string;
    validate?: (value?: string) => boolean;
  };

  const CAMPOS_BASE = [
    { field: 'codigoNcmBemMineral', label: 'Código NCM do Bem Mineral' },
    { field: 'unidadeMedida', label: 'Unidade de Medida' },
    { field: 'faseDeIndustrializacao', label: 'Fase de Industrialização' },
    { field: 'novaEspecie', label: 'Nova Espécie' },
    { field: 'codigoNcmNovaEspecie', label: 'Código NCM da Nova Espécie', validate: validarCodigoNCMNovaEspecie },
    { field: 'unidadeMedidaNovaEspecie', label: 'Unidade de Medida da Nova Espécie' },
  ];

  const CAMPOS_VALOR_REFERENCIA = [
    { field: 'valorProducaoTotal', label: 'Valor de Produção (VP)', validate: validarCampoFinanceiro },
    { field: 'teorConcentrado', label: 'Teor Concentrado (TC)', validate: validarCampoNumero },
    { field: 'teorAlimentacao', label: 'Teor Alimentação (TA)', validate: validarCampoNumero },
    { field: 'idSubstanciaFatorDeAjuste', label: 'Substância (Fatores de Ajuste)' },
  ];

  const CAMPOS_PRECO_CORRENTE = [{ field: 'precoCorrente', label: 'Preço Corrente', validate: validarCampoFinanceiro }];

  const camposObrigatorios: ValidacaoCampo[] = [...CAMPOS_BASE];

  if (formValues.tipoMetodo === EMetodoReceitaBrutaCalculada.VALOR_REFERENCIA) {
    camposObrigatorios.push(...CAMPOS_VALOR_REFERENCIA);
  } else {
    camposObrigatorios.push(...CAMPOS_PRECO_CORRENTE);
  }

  return camposObrigatorios;
};

const validarCamposObrigatorios = (formValues: IFormReceitaBrutaCalculada) => {
  const camposObrigatorios = retornarCamposObrigatorios(formValues);

  const camposInvalidos = [];

  for (const campo of camposObrigatorios) {
    let testeValidacao = validarCampoTexto;

    if (campo.validate) {
      testeValidacao = campo.validate;
    }

    if (testeValidacao(formValues[campo.field])) {
      camposInvalidos.push(campo.label);
    }
  }

  let errosValidacaoDeducoes = validarDeducoes(formValues.tributosValores || [], formValues.tributosPossiveis);
  if (errosValidacaoDeducoes !== '') {
    return errosValidacaoDeducoes;
  }

  if (camposInvalidos.length == camposObrigatorios.length) {
    return 'Por favor, preencha corretamente todos os campos';
  } else if (camposInvalidos.length >= 2) {
    return `Por favor, preencha corretamente os campos "${camposInvalidos[0]}" e "${camposInvalidos[1]}"`;
  } else if (camposInvalidos.length == 1) {
    return `Por favor, preencha corretamente o campo "${camposInvalidos[0]}"`;
  } else {
    return '';
  }
};

const validarCompraTerceiro = (formValues: IFormReceitaBrutaCalculada) => {
  if (formValues.haCompraTerceiro) {
    if (
      validarCampoNumero(formValues.quantidadeAquisicaoTerceiros) ||
      validarCampoFinanceiro(formValues.valorTotalAquisicaoTerceiros)
    ) {
      return 'Você informou que há compra de terceiro. Por favor, informe a quantidade e o valor de aquisição de terceiros.';
    }

    return '';
  } else {
    return '';
  }
};

const validarQuantidades = (formValues: IFormReceitaBrutaCalculada) => {
  const qtdConsumida = parseFloat(formatFloatToUS(formValues.quantidadeConsumidaNovaEspecie || '0'));
  const qtdUtilizada = parseFloat(formatFloatToUS(formValues.quantidadeUtilizadaDoadaOuBonificada || '0'));

  if (qtdConsumida + qtdUtilizada < NUMERO_MINIMO) {
    return 'A soma da quantidade consumida com a utilizada deve ser maior que zero';
  } else {
    return '';
  }
};

export const validarReceitaBrutaCalculada = (formValues: IFormReceitaBrutaCalculada) => {
  const validacoes = [validarCamposObrigatorios, validarQuantidades, validarCompraTerceiro];

  for (const funcao of validacoes) {
    const msgValidacao = funcao(formValues);

    if (msgValidacao !== '') {
      return msgValidacao;
    }
  }

  return '';
};
